import SEO from "@/components/SEO";
import { PageHero, Reveal, Kicker, Section, Rich } from "@/components/Bits";
import EnquiryForm from "@/components/EnquiryForm";
import { CONTACT } from "@/content/policies";
import { Mail, Phone, Building2, Clock } from "lucide-react";

const ROWS = [
  { Icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  { Icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone" },
  { Icon: Building2, label: "Registered Office", value: CONTACT.address, testid: "contact-office" },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact us"
        description="Reach the Middle Man support team by email, phone or at our registered office in Narhe, Pune, Maharashtra. Support hours: Monday to Saturday, 10 AM – 6 PM IST."
        path="/contact"
      />
      <PageHero
        kicker="Contact us"
        title="We're here to help."
        subtitle="Support, business enquiries, grievance & compliance — one team, prompt reply."
      />

      <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr]">
          <div className="space-y-12">
            <Reveal>
              <div data-testid="contact-rows">
                <Kicker>Reach us</Kicker>
                <div className="mt-6 divide-y divide-line border border-line bg-white">
                  {ROWS.map((r) => (
                    <div key={r.label} className="flex gap-4 p-5">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-brand-soft">
                        <r.Icon className="h-5 w-5 text-brand" strokeWidth={1.8} />
                      </span>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-mute">{r.label}</p>
                        {r.href ? (
                          <a href={r.href} data-testid={r.testid} className="mt-1 block text-sm font-semibold text-ink transition-colors hover:text-brand">
                            {r.value}
                          </a>
                        ) : (
                          <p data-testid={r.testid} className="mt-1 text-sm leading-relaxed text-ink">{r.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal>
              <Section title="Company">
                <Rich text={`**${CONTACT.company}** — operator of the Middle Man brand.`} />
                <Rich text={`For legal notices please write to **${CONTACT.email}** with subject prefix “LEGAL —” and copy the postal address above.`} />
              </Section>
            </Reveal>

            <Reveal>
              <Section title="Support hours">
                <div className="flex gap-3">
                  <Clock className="mt-1 h-4 w-4 shrink-0 text-brand" />
                  <Rich text={`Support responds **${CONTACT.hours}**. Urgent safety issues are triaged 24×7 through the in-app “Emergency Assistance” flow.`} />
                </div>
              </Section>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div>
              <Kicker>Write to us</Kicker>
              <h2 className="mt-5 font-serif text-2xl font-semibold tracking-tight md:text-3xl">Send a message</h2>
              <p className="mb-8 mt-3 text-sm leading-relaxed text-mute">
                Every message gets a reference number and an acknowledgement within 48 working hours.
              </p>
              <EnquiryForm kind="contact" testid="contact-form" buttonLabel="Send message" />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
